import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useI18n } from "@/lib/i18n";
import { TID } from "@/lib/testIds";
import { Menu, X, ArrowUpRight } from "lucide-react";
import LogoMark from "@/components/LogoMark";

export default function Header() {
  const { t, lang, setLang } = useI18n();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const navItems = [
    { to: "/", label: t("nav.home"), tid: TID.navHome },
    { to: "/despre", label: t("nav.about"), tid: TID.navAbout },
    { to: "/solutii", label: t("nav.solutions"), tid: TID.navSolutions },
    { to: "/expertiza", label: t("nav.expertise"), tid: TID.navExpertise },
    { to: "/contact", label: t("nav.contact"), tid: TID.navContact },
  ];

  return (
    <header
      data-testid={TID.header}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/90 backdrop-blur-xl border-b border-[color:var(--gc-line)]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
        <div className="h-24 flex items-center justify-between gap-6">
          {/* Logo */}
          <Link
            to="/"
            data-testid={TID.headerLogo}
            className="flex items-center gap-3 group shrink-0"
            aria-label="GlassCore"
            onClick={(e) => {
              if (location.pathname === "/") {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: "smooth" });
              }
            }}
          >
            <LogoMark className="h-[60px] w-auto" dark={!scrolled} />
          </Link>

          {/* Center nav */}
          <nav className="hidden xl:flex items-center gap-9">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={item.tid}
                className={({ isActive }) =>
                  `gc-link text-[13px] uppercase tracking-[0.14em] font-medium transition-colors ${
                    scrolled
                      ? isActive
                        ? "text-[color:var(--gc-ink)]"
                        : "text-[color:var(--gc-steel)] hover:text-[color:var(--gc-ink)]"
                      : isActive
                        ? "text-white"
                        : "text-white/75 hover:text-white"
                  }`
                }
              >
                {({ isActive }) => (
                  <span data-active={isActive}>{item.label}</span>
                )}
              </NavLink>
            ))}
          </nav>

          {/* Right: lang + CTA */}
          <div className="flex items-center gap-3">
            <div
              data-testid={TID.langToggle}
              className={`hidden sm:flex items-center text-[12px] font-mono tracking-wider border transition-colors ${
                scrolled ? "border-[color:var(--gc-line)]" : "border-white/30"
              }`}
            >
              <button
                data-testid={TID.langRo}
                onClick={() => setLang("ro")}
                className={`px-2.5 py-1.5 transition-colors ${
                  lang === "ro"
                    ? scrolled
                      ? "bg-[color:var(--gc-ink)] text-white"
                      : "bg-white text-[color:var(--gc-ink)]"
                    : scrolled
                      ? "text-[color:var(--gc-steel)] hover:text-[color:var(--gc-ink)]"
                      : "text-white/75 hover:text-white"
                }`}
                aria-pressed={lang === "ro"}
              >
                RO
              </button>
              <button
                data-testid={TID.langEn}
                onClick={() => setLang("en")}
                className={`px-2.5 py-1.5 transition-colors ${
                  lang === "en"
                    ? scrolled
                      ? "bg-[color:var(--gc-ink)] text-white"
                      : "bg-white text-[color:var(--gc-ink)]"
                    : scrolled
                      ? "text-[color:var(--gc-steel)] hover:text-[color:var(--gc-ink)]"
                      : "text-white/75 hover:text-white"
                }`}
                aria-pressed={lang === "en"}
              >
                EN
              </button>
              <button
                data-testid={TID.langTr}
                onClick={() => setLang("tr")}
                className={`px-2.5 py-1.5 transition-colors ${
                  lang === "tr"
                    ? scrolled
                      ? "bg-[color:var(--gc-ink)] text-white"
                      : "bg-white text-[color:var(--gc-ink)]"
                    : scrolled
                      ? "text-[color:var(--gc-steel)] hover:text-[color:var(--gc-ink)]"
                      : "text-white/75 hover:text-white"
                }`}
                aria-pressed={lang === "tr"}
              >
                TR
              </button>
            </div>

            <Link
              data-testid={TID.headerCta}
              to="/contact"
              className={`gc-btn hidden md:inline-flex transition-colors ${
                scrolled ? "gc-btn-primary" : "bg-white text-[color:var(--gc-ink)] hover:bg-white/90"
              }`}
            >
              <span>{t("common.cta_primary")}</span>
              <ArrowUpRight className="w-4 h-4 gc-arrow" />
            </Link>

            <button
              data-testid={TID.mobileMenuBtn}
              className={`xl:hidden p-2 -mr-2 transition-colors ${
                scrolled ? "text-[color:var(--gc-ink)]" : "text-white"
              }`}
              onClick={() => setOpen((o) => !o)}
              aria-label={open ? t("common.menu_close") : t("common.menu_open")}
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          data-testid={TID.mobileMenu}
          className="xl:hidden bg-white border-t border-[color:var(--gc-line)] px-6 py-6"
        >
          <div className="flex flex-col gap-4">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`mobile-${item.tid}`}
                className="text-[15px] uppercase tracking-[0.12em] font-medium text-[color:var(--gc-ink)] py-1"
              >
                {item.label}
              </Link>
            ))}
            <div className="flex items-center gap-2 pt-3 border-t border-[color:var(--gc-line)]">
              <button
                onClick={() => setLang("ro")}
                className={`px-3 py-1.5 text-xs font-mono ${lang === "ro" ? "bg-[color:var(--gc-ink)] text-white" : "border border-[color:var(--gc-line)]"}`}
              >
                RO
              </button>
              <button
                onClick={() => setLang("en")}
                className={`px-3 py-1.5 text-xs font-mono ${lang === "en" ? "bg-[color:var(--gc-ink)] text-white" : "border border-[color:var(--gc-line)]"}`}
              >
                EN
              </button>
              <button
                onClick={() => setLang("tr")}
                className={`px-3 py-1.5 text-xs font-mono ${lang === "tr" ? "bg-[color:var(--gc-ink)] text-white" : "border border-[color:var(--gc-line)]"}`}
              >
                TR
              </button>
            </div>
            <Link
              to="/contact"
              className="gc-btn gc-btn-primary mt-2 justify-center"
            >
              {t("common.cta_primary")}
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
